import {useMemo} from "react";

import type {RevaValuationPurpose} from "@reva/shared/valuation-purpose";

import {selectPermissionCheck} from "../redux/authSelectors";
import {selectPermissions, selectShowEvaPlusLinks} from "../redux/authSlice";
import {selectIsSnapshot, selectLifeCycleState, selectReadOnly} from "../redux/persistenceSlice";
import {useRevaSelector} from "../redux/revaHooks";
import {
    isContentWriteAllowedInState,
    PERMISSION_RULE_FINALIZATION,
    type PermissionRequirement,
    type PermissionRule,
    resolvePermissionRequirement,
} from "../util/permissions";
import {creatablePurposes, isResearchOnly} from "../util/purposeResolution";
import {RevaPermission} from "../util/revaPermission";

/** Check a permission rule against the current user's permissions. */
export const useCheckPermission = (rule: PermissionRule | undefined): boolean =>
    useRevaSelector(selectPermissionCheck(rule));

/**
 * Snapshot-aware variant of {@link useCheckPermission}: resolves a {@link PermissionRequirement}
 * against the currently loaded snapshot state and then checks the resulting rule. A requirement
 * side left unset (e.g. no `snapshot` rule) means "no permission required" in that state.
 */
export const useCheckPermissionRequirement = (requirement: PermissionRequirement | undefined): boolean => {
    const isSnapshot = useRevaSelector(selectIsSnapshot);
    return useCheckPermission(resolvePermissionRequirement(requirement, isSnapshot));
};

/** Check if the current user has admin permission. */
export const useCheckPermissionAdmin = (): boolean => useCheckPermission(RevaPermission.ADMINS);

/**
 * Ob Links nach EVA+ gezeigt werden (#19302 S-B9): EVA+ ist VALUE-intern, reva-rest entscheidet pro Lizenz
 * (value*-Lizenzen, Vollzugriff, Setting „ValueAG interne Lizenz“); ADMINS einer fremden Lizenz sehen keinen Link.
 * Meldet reva-rest das Feld noch nicht (`undefined`, älteres reva-rest), bleibt der Link sichtbar.
 */
export const useShowEvaPlusLinks = (): boolean => useRevaSelector(selectShowEvaPlusLinks) !== false;

/**
 * Check if the current user is a revision-readonly user (may only view read-only snapshots and
 * must not perform write operations). Used to hide GUI actions such as attaching, detaching or
 * commissioning value orders / inspections.
 */
export const useIsRevisionReadOnly = (): boolean => useCheckPermission(RevaPermission.REVA_LIFECYCLE_REVISION_READONLY);

/**
 * Ob der User die geladene Bewertung in ihrem Zustand inhaltlich bearbeiten darf (#19302 V-B8; Spiegel von
 * LifeCycleGuards#isContentWriteAllowedInState): nicht bei geladenem Snapshot oder in Final ({@link selectReadOnly}),
 * nie mit der Read-only-Revisionsrolle, in PreFinal/Correction nur mit Abschlussrecht
 * ({@link isContentWriteAllowedInState}). Steuert Beauftragen, Verknüpfen und Entkoppeln von Value-Aufträgen, die
 * reva-rest sonst erst beim Absenden ablehnt.
 */
export const useCanWriteValuationContent = (): boolean => {
    const readOnly = useRevaSelector(selectReadOnly);
    const lifeCycleState = useRevaSelector(selectLifeCycleState);
    const isRevisionReadOnly = useIsRevisionReadOnly();
    const canFinalize = useCheckPermission(PERMISSION_RULE_FINALIZATION);
    return !readOnly && !isRevisionReadOnly && isContentWriteAllowedInState(lifeCycleState, canFinalize);
};

/** #19481: Die Zwecke, mit denen der User neue REVA-IDs anlegen darf (leer = er darf gar nicht anlegen). */
export const useCreatablePurposes = (): RevaValuationPurpose[] => {
    const permissions = useRevaSelector(selectPermissions);
    return useMemo(() => creatablePurposes(permissions), [permissions]);
};

/** #19481: Research-only-User (siehe {@link isResearchOnly}) — sieht auf der Startseite nur den Research-Reiter. */
export const useIsResearchOnly = (): boolean => {
    const permissions = useRevaSelector(selectPermissions);
    return useMemo(() => isResearchOnly(permissions), [permissions]);
};
